// App.js — Forrester Call Prep | Client-Safe Edition
import React, { useMemo, useState } from "react";

// --- Design Tokens ---
const TOKENS = {
  // Brand Colors
  green: "#74C043",
  greenLight: "#ECFDF3",
  navy: "#0B1A3C", // Deep background
  navyLight: "#182B54", // Card background for modal
  blueAccent: "#3B82F6", 
  
  // Text Colors
  ink: "#111827",
  inkSoft: "#4B5563",
  inkLighter: "#9CA3AF",
  white: "#FFFFFF",
  
  // UI Elements
  bg: "#F7FAFC",
  card: "#FFFFFF",
  line: "#E5E7EB",
  warning: "#F59E0B",
  success: "#10B981",
};

// --- Icons ---
const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

// --- Enriched Data with ABM Operator Rewrites ---
const DATA = [
  {
    id: "Q1",
    origin: "Coursera",
    question: "When it comes to building our account-based buying journey stages in Demandbase, do you have relative benchmarks for companies in our industry?",
    tags: ["ABM Measurement", "Buying Journey", "Benchmarks"],
    persona: {
      role: "VP of Marketing Operations",
      lens: "Operational Efficiency",
      nightmare: [
        "Defending arbitrary conversion rates to the CFO.",
        "The Board asking 'Is this good?' and having no industry comparison.",
        "Over-engineering the funnel based on gut feel rather than data."
      ],
      success: [
        "Benchmark-backed confidence in quarterly planning.",
        "Ability to flag 'at-risk' stages early based on deviation from norm.",
        "Standardized definitions of 'MQA' that Sales accepts."
      ]
    }
  },
  {
    id: "Q2",
    origin: "Fairway",
    question: "When you see best-in-class ABM teams use journey stages, what are the most common operational actions tied to stage movement (e.g., target expansion/contraction, ad budget shifts, SDR routing, content offers)? Do you recommend setting explicit stage-exit criteria and play triggers so the model drives action—not just reporting?",
    tags: ["Plays & Triggers", "ABM Operations", "Journey Optimization"],
    persona: {
      role: "Chief Revenue Officer",
      lens: "Plays & Triggers",
      nightmare: [
        "Marketing reports looking green while revenue misses target.",
        "Vanity metrics that don't inform budget allocation.",
        "Sales teams ignoring 'warm' accounts because they don't trust the score."
      ],
      success: [
        "Automated budget shifts towards high-velocity segments.",
        "Sales changing their outreach cadence based on journey stage.",
        "Metrics acting as a dashboard for action, not a rearview mirror."
      ]
    }
  },
  {
    id: "Q3",
    origin: "Coursera",
    question: "We are building a revenue forecast model. Have you seen successful frameworks for quantifying the impact of ad-hoc campaign requests on the primary revenue target?",
    tags: ["Revenue Forecasting", "Attribution", "Campaign Impact"],
    persona: {
      role: "Head of Demand Gen",
      lens: "Resource Allocation",
      nightmare: [
        "Sales leadership demanding 'air cover' that distracts from core strategy.",
        "Inability to prove that 'random acts of marketing' hurt the bottom line.",
        "Missing the primary commit because resources were diverted."
      ],
      success: [
        "A clear 'cost of distraction' model to push back on ad-hoc requests.",
        "Predictable revenue modeling that isolates variable campaigns.",
        "Data-driven defense of the primary campaign roadmap."
      ]
    }
  },
  {
    id: "Q4",
    origin: "Fairway",
    question: "For ABM forecasting, have you seen teams use scenario-based planning (base / stretch / downside) for incremental pipeline influenced by ABM—especially when intake includes ad-hoc requests? If yes, what’s a practical way to structure that so it’s usable in quarterly planning?",
    tags: ["Scenario Planning", "Risk", "Revenue Operations"],
    persona: {
      role: "CFO / Finance Partner",
      lens: "Scenario Planning",
      nightmare: [
        "Marketing committing to a single number that is essentially a guess.",
        "Lack of visibility into the volatility of the pipeline.",
        "Surprise misses at the end of the quarter."
      ],
      success: [
        "Forecasts presented as probability ranges (P90/P50).",
        "Understanding the 'beta' or volatility of different campaign types.",
        "Credibility with the Finance office."
      ]
    }
  },
  {
    id: "Q5",
    origin: "Coursera",
    question: "In your revised Revenue Waterfall, how do you recommend measuring the effectiveness of Buying Group Engagement?",
    tags: ["Buying Groups", "Revenue Waterfall", "Engagement"],
    persona: {
      role: "Director of ABM",
      lens: "Engagement Depth",
      nightmare: [
        "Engaging only one champion while the rest of the committee blocks the deal.",
        "High engagement scores but low deal velocity.",
        "Sales complaining that 'leads' aren't ready to buy."
      ],
      success: [
        "Tracking 'Group Coverage' % not just individual leads.",
        "Visualizing the buying committee health before handoff.",
        "Correlating multi-thread engagement with deal speed."
      ]
    }
  },
  {
    id: "Q6",
    origin: "Fairway",
    question: "For Buying Group Engagement, do you recommend scoring based on coverage + recency + momentum (not just a single engagement score)? What signals have you seen correlate most with buying-group progression (multi-threading, role coverage, key-page intent, meeting-set velocity)?",
    tags: ["Momentum & Coverage", "Funnel Velocity"],
    persona: {
      role: "Data Science Lead",
      lens: "Momentum Scoring",
      nightmare: [
        "Static scores that don't capture momentum (or loss thereof).",
        "False positives where a score is high but engagement is months old.",
        "Treating a stalled deal the same as an accelerating one."
      ],
      success: [
        "Vector-based measurement (Direction + Magnitude).",
        "Alerting sales when velocity drops, even if score is high.",
        "Dynamic scoring models."
      ]
    }
  },
  {
    id: "Q7",
    origin: "Coursera",
    question: "From a technical implementation perspective, how have organizations successfully mapped buying groups pre-opportunity?",
    tags: ["Stage 0", "Buying Group Mapping", "Multi-Product"],
    persona: {
      role: "Salesforce Architect",
      lens: "System Architecture",
      nightmare: [
        "Creating thousands of empty Opportunities just to track groups.",
        "Polluting the pipeline with 'Stage 0' junk.",
        "Sales reps ignoring the data because it's too messy."
      ],
      success: [
        "Clean object model (e.g., a custom 'Buying Group' object).",
        "Seamless conversion from Pre-Opp group to Opportunity.",
        "System performance remains high."
      ]
    }
  },
  {
    id: "Q8",
    origin: "Fairway",
    question: "Pre-opportunity, what’s the best practice for making buying group / account insights actionable in day-to-day workflows for SDRs and AEs (views, queues, alerts, tasks)—without creating noisy “Stage 0” clutter in Salesforce?",
    tags: ["Workflow & Visibility", "Sales Enablement"],
    persona: {
      role: "VP of Sales Strategy",
      lens: "Sales Workflow",
      nightmare: [
        "Sales reps flying blind until an Opportunity is officially created.",
        "Marketing data sitting in a silo that Sales can't see.",
        "Late entry into deals because early signals were ignored."
      ],
      success: [
        "Sales working 'Above the Funnel' effectively.",
        "A 'Radar' view for Sales Development Reps (SDRs).",
        "Marketing acting as the early warning system."
      ]
    }
  },
  {
    id: "Q9",
    origin: "Coursera",
    question: "Our Salesforce parent/child account hierarchies are inconsistent. What short-term approaches do you recommend?",
    tags: ["Salesforce Architecture", "Data Hygiene", "Hierarchy"],
    persona: {
      role: "CRM Admin",
      lens: "Data Governance",
      nightmare: [
        "Signals rolling up to the wrong parent company.",
        "Sales fighting over commission splits due to bad hierarchy.",
        "Spending 6 months on data cleanup with no results to show."
      ],
      success: [
        "A workable 'Band-Aid' solution that allows reporting now.",
        "Clear rules of engagement for multi-national accounts.",
        "Progressive profiling of hierarchy data."
      ]
    }
  },
  {
    id: "Q10",
    origin: "Fairway",
    question: "How do you recommend ABM teams design an engagement/intent model that Sales will trust—especially pre-opportunity—when signals come from multiple sources (first-party web, ads, intent, email)? Any best practices for transparent weighting (what counts most, what decays, what’s excluded)?",
    tags: ["Score Trust", "Signal Hygiene", "Intent Signals"],
    persona: {
      role: "Marketing Analyst",
      lens: "Score Trust",
      nightmare: [
        "Treating a generic display click the same as a pricing page visit.",
        "Sales losing trust because 'Intent' data was just a bot.",
        "Noise drowning out the signal."
      ],
      success: [
        "A weighted scoring model (First Party > Third Party).",
        "Confidence intervals attached to lead scores.",
        "Transparent scoring logic."
      ]
    }
  },
  {
    id: "Q11",
    origin: "Fairway",
    question: "In mature ABM programs, where do you recommend standardizing rules versus allowing human judgment (account selection, buying-group definition, stage progression, SLA handoffs)? What governance/guardrails prevent misfires while still keeping the program scalable?",
    tags: ["Governance", "SLA Handoffs", "Scalability"],
    persona: {
      role: "Marketing Ops Manager",
      lens: "Governance & SLAs",
      nightmare: [
        "Automation running wild and sending irrelevant emails to CEOs.",
        "Bottlenecks where humans are doing data entry.",
        "The 'Uncanny Valley' of bad automated personalization."
      ],
      success: [
        "Humans focused on Strategy and Exception Handling.",
        "Machines focused on Execution and Scale.",
        "Clear 'Circuit Breakers' in the automation flow."
      ]
    }
  },
  {
    id: "Q12",
    origin: "Fairway",
    question: "How often do you see best-in-class teams recalibrate their ABM stage definitions, thresholds, and scoring models (monthly/quarterly)? What’s the recommended approach to testing and validating changes without breaking reporting continuity?",
    tags: ["Model Calibration", "Optimization", "Reporting"],
    persona: {
      role: "CMO",
      lens: "Model Calibration",
      nightmare: [
        "Doing the same thing next year because 'it's how we've always done it'.",
        "Stagnant conversion rates.",
        "Competitors evolving faster than we are."
      ],
      success: [
        "Agile Marketing approach to GTM.",
        "Quarterly retrospectives that actually change strategy.",
        "Institutional knowledge that survives staff turnover."
      ]
    }
  }
];

// --- Components ---

function Chip({ children, tone = "neutral" }) {
  const map = {
    neutral: { bg: "#EEF2FF", fg: "#3730A3", border: "#C7D2FE" },
    coursera: { bg: "#FFF7ED", fg: "#9A3412", border: "#FED7AA" },
    fairway: { bg: "#ECFDF3", fg: "#166534", border: "#BBF7D0" },
    tag: { bg: "#F3F4F6", fg: TOKENS.inkSoft, border: TOKENS.line },
  };
  const t = map[tone] || map.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "4px 8px",
      borderRadius: 999, background: t.bg, color: t.fg,
      border: `1px solid ${t.border}`, fontSize: 11, fontWeight: 600,
      lineHeight: 1, whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

function Button({ children, onClick, variant = "primary", icon }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "8px 14px", borderRadius: 8,
    border: `1px solid transparent`,
    fontWeight: 600, cursor: "pointer", fontSize: 13,
    transition: "all 0.2s",
  };
  const variants = {
    primary: { background: TOKENS.navy, color: "white", borderColor: TOKENS.navy },
    ghost: { background: "transparent", color: TOKENS.inkSoft, borderColor: TOKENS.line },
    outline: { background: "white", color: TOKENS.navy, borderColor: TOKENS.line },
    accent: { background: TOKENS.blueAccent, color: "white", borderColor: TOKENS.blueAccent }, 
    glass: { background: "rgba(255,255,255,0.1)", color: "white", borderColor: "rgba(255,255,255,0.2)" },
  };
  return (
    <button onClick={onClick} style={{ ...base, ...variants[variant] }}>
      {icon} {children}
    </button>
  );
}

// --- The "Context Lens" Modal Component ---
function ContextLens({ data, onClose }) {
  if (!data) return null;

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "rgba(11, 26, 60, 0.85)", // Navy Overlay
      backdropFilter: "blur(8px)",
      zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20
    }}>
      <div style={{
        backgroundColor: TOKENS.navy,
        width: "100%", maxWidth: 900,
        borderRadius: 24,
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
        border: "1px solid rgba(255,255,255,0.1)",
        overflow: "hidden",
        position: "relative",
        animation: "slideUp 0.3s ease-out"
      }}>
        {/* Header */}
        <div style={{ 
          padding: "32px 40px", 
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start"
        }}>
          <div>
            <div style={{ 
              display: "inline-block", padding: "6px 12px", borderRadius: 99, 
              background: TOKENS.blueAccent, color: "white", fontSize: 12, 
              fontWeight: 700, letterSpacing: 1, marginBottom: 16 
            }}>
              {data.persona.lens.toUpperCase()} LENS
            </div>
            <h2 style={{ color: "white", fontSize: 32, margin: 0, fontWeight: 600 }}>
              {data.persona.role}
            </h2>
            <div style={{ color: "rgba(255,255,255,0.6)", marginTop: 8, fontSize: 16 }}>
               Context for Question {data.id}
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{ 
              background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", 
              width: 40, height: 40, display: "flex", alignItems: "center", 
              justifyContent: "center", color: "white", cursor: "pointer" 
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", minHeight: 400 }}>
          
          {/* Left: Pain Points */}
          <div style={{ padding: 40, borderRight: "1px solid rgba(255,255,255,0.1)" }}>
            <h3 style={{ 
              color: TOKENS.warning, fontSize: 13, letterSpacing: 1.2, fontWeight: 700, 
              textTransform: "uppercase", marginBottom: 24, display: "flex", alignItems: "center", gap: 8 
            }}>
              ⚠️ What keeps them up at night
            </h3>
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {data.persona.nightmare.map((item, i) => (
                <li key={i} style={{ 
                  color: "rgba(255,255,255,0.9)", marginBottom: 16, fontSize: 16, lineHeight: 1.6,
                  display: "flex", gap: 12
                }}>
                  <span style={{ color: TOKENS.warning }}>•</span>
                  {item}
                </li>
              ))}
            </ul>

            <div style={{ marginTop: 40 }}>
              <h3 style={{ 
                color: "rgba(255,255,255,0.5)", fontSize: 13, letterSpacing: 1.2, 
                fontWeight: 700, textTransform: "uppercase", marginBottom: 16 
              }}>
                Critical Question
              </h3>
              <div style={{ 
                fontStyle: "italic", fontSize: 18, color: "white", lineHeight: 1.5,
                borderLeft: `3px solid ${TOKENS.blueAccent}`, paddingLeft: 16 
              }}>
                "{data.question}"
              </div>
            </div>
          </div>

          {/* Right: Success Criteria */}
          <div style={{ padding: 40, backgroundColor: "rgba(255,255,255,0.03)" }}>
            <h3 style={{ 
              color: TOKENS.success, fontSize: 13, letterSpacing: 1.2, fontWeight: 700, 
              textTransform: "uppercase", marginBottom: 24, display: "flex", alignItems: "center", gap: 8 
            }}>
              ✓ What success looks like
            </h3>
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {data.persona.success.map((item, i) => (
                <li key={i} style={{ 
                  color: "rgba(255,255,255,0.9)", marginBottom: 16, fontSize: 16, lineHeight: 1.6,
                  display: "flex", gap: 12 
                }}>
                  <div style={{ 
                    minWidth: 20, height: 20, borderRadius: "50%", border: `1px solid ${TOKENS.success}`,
                    display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4
                  }}>
                    <div style={{ width: 10, height: 10, background: TOKENS.success, borderRadius: "50%" }} />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div style={{ 
          padding: "20px 40px", borderTop: "1px solid rgba(255,255,255,0.1)",
          backgroundColor: "#081226", display: "flex", justifyContent: "flex-end"
        }}>
          <Button variant="glass" onClick={onClose}>Close Lens</Button>
        </div>
      </div>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// --- Utils ---
function buildCopyText(rows) {
  const header = ["Question", "Origin", "Topics Covered"].join("\t");
  const lines = rows.map((r) => [r.question, r.origin, r.tags.join(", ")].join("\t"));
  return [header, ...lines].join("\n");
}

export default function App() {
  const [originFilter, setOriginFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState("All");
  const [copied, setCopied] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState(null);

  const activeQuestionData = useMemo(() => {
    return DATA.find(d => d.id === activeQuestionId);
  }, [activeQuestionId]);

  const allTags = useMemo(() => {
    const set = new Set();
    DATA.forEach((d) => d.tags.forEach((t) => set.add(t)));
    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DATA.filter((row) => {
      const okOrigin = originFilter === "All" ? true : row.origin === originFilter;
      const okTag = tagFilter === "All" ? true : row.tags.some((t) => t === tagFilter);
      const okQuery = !q ? true : 
        row.question.toLowerCase().includes(q) ||
        row.tags.join(" ").toLowerCase().includes(q) ||
        row.origin.toLowerCase().includes(q);
      return okOrigin && okTag && okQuery;
    });
  }, [originFilter, query, tagFilter]);

  const counts = useMemo(() => {
    const c = { All: DATA.length, Coursera: 0, Fairway: 0 };
    DATA.forEach((d) => (c[d.origin] += 1));
    return c;
  }, []);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildCopyText(filtered));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) { alert("Clipboard copy failed."); }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(180deg, #FFFFFF 0%, ${TOKENS.bg} 100%)`,
      padding: 24, color: TOKENS.ink,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        
        {/* Header Section */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 32 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 14, height: 14, borderRadius: 99, background: TOKENS.green, boxShadow: `0 0 0 4px ${TOKENS.greenLight}` }} />
              <h1 style={{ margin: 0, fontSize: 24, color: TOKENS.navy, fontWeight: 700 }}>Forrester Call Prep</h1>
            </div>
            {/* --- UPDATED CLIENT-FACING MESSAGING (SAFE) --- */}
            <p style={{ margin: "8px 0 0 26px", color: TOKENS.inkSoft, fontSize: 15 }}>
              Coursera’s Strategic Questions — enhanced with interactive context lenses to visualize key organizational personas, business impact, and operational angles.
            </p>
          </div>
          <Button onClick={onCopy} variant="primary">
            {copied ? "Copied ✓" : "Copy List"}
          </Button>
        </div>

        {/* Filters */}
        <div style={{
          display: "flex", gap: 16, padding: 16, background: "white", 
          borderRadius: 16, border: `1px solid ${TOKENS.line}`, boxShadow: "0 4px 12px rgba(0,0,0,0.03)", marginBottom: 24 
        }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: TOKENS.inkSoft, marginBottom: 4 }}>SEARCH</label>
            <input 
              value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Filter by keyword..."
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${TOKENS.line}`, fontSize: 14 }} 
            />
          </div>
          <div style={{ width: 180 }}>
             <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: TOKENS.inkSoft, marginBottom: 4 }}>ORIGIN</label>
             <select 
               value={originFilter} onChange={e => setOriginFilter(e.target.value)}
               style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${TOKENS.line}`, fontSize: 14, background: "white" }}
             >
               <option value="All">All Sources</option>
               <option value="Coursera">Coursera Only</option>
               <option value="Fairway">Fairway Only</option>
             </select>
          </div>
          <div style={{ width: 180 }}>
             <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: TOKENS.inkSoft, marginBottom: 4 }}>TOPIC</label>
             <select 
               value={tagFilter} onChange={e => setTagFilter(e.target.value)}
               style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${TOKENS.line}`, fontSize: 14, background: "white" }}
             >
               {allTags.map(t => <option key={t} value={t}>{t}</option>)}
             </select>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
           <Chip tone="coursera">Coursera: {counts.Coursera}</Chip>
           <Chip tone="fairway">Fairway: {counts.Fairway}</Chip>
           <Chip tone="neutral">Total: {counts.All}</Chip>
        </div>

        {/* Data List */}
        <div style={{ borderRadius: 16, border: `1px solid ${TOKENS.line}`, overflow: "hidden", background: "white", boxShadow: "0 10px 30px rgba(0,0,0,0.04)" }}>
          {filtered.map((row, idx) => (
            <div key={row.id} style={{ 
              display: "grid", gridTemplateColumns: "60px 1fr 180px", gap: 20, 
              padding: 20, borderBottom: `1px solid ${TOKENS.line}`,
              backgroundColor: row.origin === "Coursera" ? "#FFFCF9" : "white"
            }}>
              {/* ID Column */}
              <div style={{ fontWeight: 800, color: TOKENS.inkLighter, fontSize: 13, paddingTop: 4 }}>{row.id}</div>
              
              {/* Main Content */}
              <div>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <Chip tone={row.origin.toLowerCase()}>{row.origin}</Chip>
                  {row.tags.map(t => <Chip key={t} tone="tag">{t}</Chip>)}
                </div>
                <div style={{ fontSize: 15, lineHeight: 1.5, color: TOKENS.ink, fontWeight: 500 }}>
                  {row.question}
                </div>
              </div>

              {/* Action Column */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                <Button 
                  variant="accent" 
                  icon={<EyeIcon />}
                  onClick={() => setActiveQuestionId(row.id)}
                >
                  View Context
                </Button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: TOKENS.inkSoft }}>No questions found.</div>}
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 40, color: TOKENS.inkLighter, fontSize: 12 }}>
          Confidential Call Prep Material
        </div>

      </div>
      
      {/* Modal Injection */}
      <ContextLens data={activeQuestionData} onClose={() => setActiveQuestionId(null)} />
    </div>
  );
}
